/**
 * Tenant resolution from the host name.
 *
 * The canonical EduViaOS hosting model is one tenant per subdomain
 * (`acme-school.eduviaos.app`, `dehradun-public.eduviaos.app`). The slug
 * lives in the URL so the user never has to type their institution ID
 * on the login form, and so the BE can scope queries from the request
 * envelope (X-Tenant-Slug header) instead of trusting client body
 * fields.
 *
 * In local dev, the same shape works: `acme-school.localhost:3001` is
 * resolved to 127.0.0.1 by every modern browser without /etc/hosts
 * edits (RFC 6761 — `.localhost` is reserved as loopback).
 *
 * In stage previews (single-level subdomain, free CF SSL), tenants are
 * encoded as `<tenant>--ia-pr-N.eduviaos.com`. The double-dash separates
 * the tenant slug (which itself may contain single dashes, e.g.
 * `acme-school`) from the preview marker.
 *
 * Reserved subdomains never represent a tenant — they're platform
 * surfaces. Keep this list in sync with TRD-17 §17.9.
 */

const RESERVED_SUBDOMAINS = new Set([
  "www",
  "api",
  "admin",
  "static",
  "assets",
  "cdn",
  "status",
  "docs",
  "blog",
  "app",
  "auth",
  "id",
]);

// Stage marker patterns — these encode a portal + tenant (or PR number)
// into a single subdomain so free Cloudflare Universal SSL (which only
// covers one wildcard level) works without paid Advanced Certificate
// Manager.
//
// Two flavours coexist:
//   - PR previews (ephemeral, per-PR): `<tenant>--ia-pr-N`,
//     `<tenant>--apply-pr-N`, `<tenant>--portal-pr-N`, `sa-pr-N`, `api-pr-N`
//   - Permanent stage (always-on, tracks the `stage` branch):
//     `<tenant>--ia-stage`, `<tenant>--apply-stage`,
//     `<tenant>--portal-stage`, `sa-stage`, `api-stage`
//
// Order matters in the matcher below: portal-specific patterns (apply,
// ia, portal) MUST be tested BEFORE the generic `(.+?)--…` shape would
// otherwise swallow them. Each pattern uses a non-greedy capture so
// multi-dash tenant slugs like `acme-school` resolve correctly:
//   acme-school--apply-stage   ->  apply,  tenant=acme-school
//   acme-school--ia-pr-12      ->  ia,     tenant=acme-school
//   acme-school--portal-stage  ->  portal, tenant=acme-school
const STAGE_IA_PR_RE = /^(.+?)--ia-pr-\d+$/i; // <tenant>--ia-pr-N
const STAGE_APPLY_PR_RE = /^(.+?)--apply-pr-\d+$/i; // <tenant>--apply-pr-N (D2d)
const STAGE_PORTAL_PR_RE = /^(.+?)--portal-pr-\d+$/i; // <tenant>--portal-pr-N (P4)
const STAGE_SA_PR_RE = /^sa-pr-\d+$/i; // sa-pr-N (super-admin, no tenant)
const STAGE_API_PR_RE = /^api-pr-\d+$/i; // api-pr-N (BE; never loaded in FE)

const STAGE_IA_PERMA_RE = /^(.+?)--ia-stage$/i; // <tenant>--ia-stage
const STAGE_APPLY_PERMA_RE = /^(.+?)--apply-stage$/i; // <tenant>--apply-stage (D2d)
const STAGE_PORTAL_PERMA_RE = /^(.+?)--portal-stage$/i; // <tenant>--portal-stage (P4)
const STAGE_SA_PERMA_RE = /^sa-stage$/i; // sa-stage
const STAGE_API_PERMA_RE = /^api-stage$/i; // api-stage (BE; never in FE)

// Bare portal hosts (no tenant prefix) are platform surfaces, not
// tenants — the SPA falls back to its default tenant there. They are
// NOT in RESERVED_SUBDOMAINS (that list is a single label), so they get
// their own reserved check alongside sa-/api-.
const STAGE_PORTAL_BARE_PR_RE = /^portal-pr-\d+$/i; // portal-pr-N (pre-P4 back-compat)
const STAGE_PORTAL_BARE_PERMA_RE = /^portal-stage$/i; // portal-stage (pre-P4 back-compat)

export interface TenantContext {
  /** The tenant slug (e.g. `acme-school`) or null when no subdomain is present. */
  slug: string | null;
  /** Human-readable label derived from the slug (e.g. `Acme School`). */
  label: string | null;
  /** Whether the current host is a reserved/platform subdomain (api, admin, etc.). */
  reserved: boolean;
}

/**
 * Extract the tenant context from a hostname.
 *
 * Examples:
 *   acme-school.eduviaos.app                  → { slug: "acme-school", label: "Acme School", reserved: false }
 *   acme-school.localhost                     → { slug: "acme-school", label: "Acme School", reserved: false }
 *   acme--ia-pr-14.eduviaos.com               → { slug: "acme", label: "Acme", reserved: false }
 *   acme-school--apply-stage.eduviaos.com     → { slug: "acme-school", label: "Acme School", reserved: false }
 *   acme-school--apply-pr-58.eduviaos.com     → { slug: "acme-school", label: "Acme School", reserved: false }
 *   acme-school--portal-stage.eduviaos.com    → { slug: "acme-school", label: "Acme School", reserved: false }
 *   quiet-corner--portal-pr-14.eduviaos.com   → { slug: "quiet-corner", label: "Quiet Corner", reserved: false }
 *   portal-stage.eduviaos.com (bare)          → { slug: null, label: null, reserved: true } (SPA default tenant)
 *   sa-pr-14.eduviaos.com                     → { slug: null, label: null, reserved: true }
 *   apply-stage.eduviaos.com (deprecated)     → { slug: null, label: null, reserved: false } (lands on landing site post-D2d)
 *   localhost                                 → { slug: null, label: null, reserved: false }
 *   www.eduviaos.app                          → { slug: null, label: null, reserved: true }
 *   eduviaos.app                              → { slug: null, label: null, reserved: false }
 */
export function parseTenantFromHost(hostname: string): TenantContext {
  // Strip port if present (e.g. `acme-school.localhost:3001` → `acme-school.localhost`)
  const host = hostname.split(":")[0]?.toLowerCase() ?? "";
  if (!host) return { slug: null, label: null, reserved: false };

  const parts = host.split(".");

  // No subdomain: bare `localhost`, bare `eduviaos.app`, single label.
  if (parts.length < 2) return { slug: null, label: null, reserved: false };

  // For 2-label hosts ending in `.localhost` (e.g. `acme.localhost`) the
  // first label IS the subdomain. Otherwise we need 3+ labels.
  let candidate: string | null = null;
  if (parts[parts.length - 1] === "localhost" && parts.length >= 2) {
    candidate = parts[0] ?? null;
  } else if (parts.length >= 3) {
    candidate = parts[0] ?? null;
  }

  if (!candidate) return { slug: null, label: null, reserved: false };

  // Stage shapes (PR previews + permanent stage) — checked BEFORE the
  // generic reserved list because `sa-pr-14`, `api-pr-14`, `sa-stage`,
  // `api-stage` aren't in RESERVED_SUBDOMAINS but are still platform
  // surfaces, not tenants.
  if (
    STAGE_API_PR_RE.test(candidate) ||
    STAGE_SA_PR_RE.test(candidate) ||
    STAGE_API_PERMA_RE.test(candidate) ||
    STAGE_SA_PERMA_RE.test(candidate) ||
    STAGE_PORTAL_BARE_PR_RE.test(candidate) ||
    STAGE_PORTAL_BARE_PERMA_RE.test(candidate)
  ) {
    return { slug: null, label: null, reserved: true };
  }
  // Per-tenant portal subdomains. We try IA + apply + portal patterns in
  // turn; all yield the same `{ slug, label }` shape regardless of which
  // portal the host belongs to, because the slug IS the only piece an
  // SPA needs to scope its API calls.
  const portalMatch =
    candidate.match(STAGE_IA_PR_RE) ??
    candidate.match(STAGE_IA_PERMA_RE) ??
    candidate.match(STAGE_APPLY_PR_RE) ??
    candidate.match(STAGE_APPLY_PERMA_RE) ??
    candidate.match(STAGE_PORTAL_PR_RE) ??
    candidate.match(STAGE_PORTAL_PERMA_RE);
  if (portalMatch && portalMatch[1]) {
    const slug = portalMatch[1];
    return {
      slug,
      label: slugToLabel(slug),
      reserved: false,
    };
  }

  if (RESERVED_SUBDOMAINS.has(candidate)) {
    return { slug: null, label: null, reserved: true };
  }

  return {
    slug: candidate,
    label: slugToLabel(candidate),
    reserved: false,
  };
}

/**
 * Read the tenant context from the current browser location. Returns the
 * "no tenant" state when called outside a browser (SSR, tests).
 */
export function getCurrentTenant(): TenantContext {
  if (typeof window === "undefined") {
    return { slug: null, label: null, reserved: false };
  }
  return parseTenantFromHost(window.location.hostname);
}

/**
 * Convert a slug to a human label. `acme-school` → `Acme School`.
 * Title-cases each dash-separated segment.
 */
export function slugToLabel(slug: string): string {
  return slug
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
